import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/pro-solid-svg-icons';

import logo from './logo.png';
import logoWhite from './logo-white.png';
import AuthContext from 'src/contexts/AuthContext';
import ResourcesContext from 'src/contexts/ResourcesContext';
import BlogContext from 'src/contexts/BlogContext';
import ClickOutside from 'src/elements/ClickListener';
import { elRed, textPrimary } from 'src/styles/colors';
import NavigationDropdown from './NavigationDropdown';
import AccountMenu from './AccountMenu';
import {
  primaryNavigationItems,
  NAVIGATION_ACTIONS,
  DROPDOWN_NAMES,
  REGISTER_BUTTON_LABEL,
  REGISTER_BUTTON_SHORT_LABEL,
} from './navigationConfig';
import {
  Container,
  LogoContainer,
  LinkContainer,
  NavigationLink,
  DropdownTriggerLabel,
  RegisterButton,
  FullRegisterLabel,
  ShortRegisterLabel,
} from './elements';

const SALE_PATH_SEGMENT = '/bidding/';

const dropdownItemStyle = {
  textDecoration: 'none',
  color: textPrimary,
  cursor: 'pointer',
};

const Menu = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const {
    setInventoryFilterValues,
    setCurrentFilterPage,
    setCurrentItemOffset,
  } = useContext(ResourcesContext);
  const { clearBlogs } = useContext(BlogContext);

  const currentPath = useLocation().pathname;
  const onSalePath = currentPath.includes(SALE_PATH_SEGMENT);

  const isActivePath = to =>
    to === '/' ? currentPath === '/' : currentPath.startsWith(to);

  const isActiveItem = item => (
    item.children ?
      item.children.some(child => child.to && isActivePath(child.to)) :
      isActivePath(item.to)
  );

  const resetInventoryFilters = () => {
    setInventoryFilterValues({});
    setCurrentFilterPage(-1);
    setCurrentItemOffset(-1);
  };

  const actionHandlers = {
    [NAVIGATION_ACTIONS.RESET_INVENTORY_FILTERS]: resetInventoryFilters,
    [NAVIGATION_ACTIONS.CLEAR_BLOGS]: clearBlogs,
  };

  const toggleDropdown = name =>
    setOpenDropdownName(current => (current === name ? null : name));

  const closeDropdowns = () => setOpenDropdownName(null);

  // Every navigation closes any open dropdown; without this, a dropdown
  // opened before clicking a plain link stays open on the next page.
  const handleAction = item => () => {
    closeDropdowns();
    actionHandlers[item.action]?.();
  };

  const handleLogoClick = () => {
    closeDropdowns();
    resetInventoryFilters();
  };

  const renderDropdownItem = item => (
    <Link
      key={item.label}
      to={item.to}
      className='menuLink'
      style={dropdownItemStyle}
      onClick={handleAction(item)}
    >
      {item.label}
    </Link>
  );

  const renderNavigationItem = item => (
    item.children ?
      <NavigationDropdown
        key={item.label}
        trigger={
          <DropdownTriggerLabel
            $onSalePath={onSalePath}
            $active={isActiveItem(item)}
            aria-current={isActiveItem(item) ? 'page' : undefined}
          >
            {item.label}
          </DropdownTriggerLabel>
        }
        isOpen={openDropdownName === item.dropdownName}
        onToggle={() => toggleDropdown(item.dropdownName)}
      >
        {item.children.map(renderDropdownItem)}
      </NavigationDropdown> :
      <NavigationLink
        key={item.label}
        to={item.to}
        $onSalePath={onSalePath}
        $active={isActiveItem(item)}
        aria-current={isActiveItem(item) ? 'page' : undefined}
        onClick={handleAction(item)}
      >
        {item.label}
      </NavigationLink>
  );

  return (
    <ClickOutside
      onClick={closeDropdowns}
      className={'header-navigation-links'}
    >
      <Container $onSalePath={onSalePath}>
        <LogoContainer>
          <Link to="/" onClick={handleLogoClick}>
            <img src={onSalePath ? logoWhite : logo} alt="Equiplinc" />
          </Link>
        </LogoContainer>
        <LinkContainer>
          {primaryNavigationItems({ isLoggedIn }).map(renderNavigationItem)}
          {isLoggedIn ?
            <NavigationDropdown
              alignRight
              showChevron={false}
              ariaLabel="Account menu"
              isOpen={openDropdownName === DROPDOWN_NAMES.ACCOUNT}
              onToggle={() => toggleDropdown(DROPDOWN_NAMES.ACCOUNT)}
              trigger={
                <FontAwesomeIcon
                  icon={faCircleUser}
                  size='2xl'
                  style={{ color: elRed }}
                />
              }
            >
              <AccountMenu />
            </NavigationDropdown> :
            <RegisterButton to="/login">
              <FullRegisterLabel>{REGISTER_BUTTON_LABEL}</FullRegisterLabel>
              <ShortRegisterLabel>
                {REGISTER_BUTTON_SHORT_LABEL}
              </ShortRegisterLabel>
            </RegisterButton>
          }
        </LinkContainer>
      </Container>
    </ClickOutside>
  );
};

export default Menu;
